/**
 * Panel de la oportunidad. NO es modal a propósito (Board Pattern §6): el
 * tablero queda visible y las columnas siguen scrolleando mientras se trabaja
 * una carta.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Icon, type IconName } from '../Icon.js';
import { Pill } from '../PillGroup.js';
import { Button } from '../ui/Button.js';
import { IconButton } from '../ui/IconButton.js';
import { Dropdown } from '../ui/Dropdown.js';
import { LoadingState } from '../ui/LoadingState.js';
import { KVRow } from '../ui/KVRow.js';
import { ValorDeLaCarta } from './ValorDeLaCarta.js';
import { formatMoney } from '../../lib/money.js';
import { relTime } from '../../lib/relTime.js';
import { parseServerDate } from '../../lib/date.js';
import { useToast } from '../ui/Toast.js';
import {
  useOpportunity, useMoveOpportunity, useDeleteOpportunity, useUpdateOpportunity,
  type FunnelStage, type OpportunityEvent,
} from '../../hooks/useFunnels.js';

interface Props {
  opportunityId: number;
  onClose: () => void;
  /** Abre el selector de contacto para vincular o crear. */
  onLinkContact: (opportunityId: number) => void;
  /** Abre el alta de cotización con el contacto de la carta precargado. */
  onCreateEstimate: (opportunityId: number) => void;
  /** Abre el alta de proyecto con el contacto de la carta precargado. */
  onCreateProject: (opportunityId: number) => void;
}

// ── Fila de vínculo ───────────────────────────────────────────────────────────
// Cuatro por panel (formulario, contacto, cotización, proyecto). Local y no en
// ui/: es la anatomía de este panel, no algo que otro módulo vaya a pedir. Los
// valores salen de los tokens --evo-subrow-*, como SubRow.

function LinkRow({ icon, label, value, emptyLabel, actionLabel, onAction }: {
  icon: IconName;
  label: string;
  /** null = todavía sin vincular; se muestra `emptyLabel` en fg-3. */
  value: string | null;
  emptyLabel: string;
  actionLabel: string;
  onAction: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onAction}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); onAction(); } }}
      style={{
        display: 'flex', alignItems: 'center',
        gap: 'var(--evo-subrow-gap)',
        minHeight: 'var(--evo-subrow-min-h)',
        boxSizing: 'border-box',
        padding: 'var(--evo-subrow-py) var(--evo-subrow-px)',
        border: '1px solid var(--evo-stroke)',
        borderRadius: 'var(--evo-r-sm)',
        cursor: 'pointer',
        transition: 'border-color 120ms linear',
      }}
    >
      <span style={{ flexShrink: 0, display: 'flex', color: 'var(--evo-fg-3)' }}>
        <Icon name={icon} size={14} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)' }}>{label}</span>
        <span
          style={{
            fontSize: 'var(--evo-list-title-fs)',
            color: value != null ? 'var(--evo-fg-1)' : 'var(--evo-fg-3)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}
        >
          {value ?? emptyLabel}
        </span>
      </div>
      <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-cyan)', whiteSpace: 'nowrap' }}>
        {actionLabel}
      </span>
    </div>
  );
}

function PanelCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    // `flexShrink: 0` no es un detalle: dentro de una columna flex los hijos se
    // encogen por defecto, y con `overflow: hidden` la tarjeta recortaba sus
    // propias filas en vez de crecer. Se veía como si los bloques se apilaran
    // uno sobre otro.
    <div style={{ flexShrink: 0, border: '1px solid var(--evo-stroke)', borderRadius: 'var(--evo-r-md)', overflow: 'hidden' }}>
      <div
        style={{
          padding: 'var(--evo-card-head-pad)',
          background: 'var(--evo-bg-2)',
          borderBottom: '1px solid var(--evo-stroke)',
          display: 'flex', alignItems: 'center', gap: 'var(--evo-card-head-gap)',
        }}
      >
        <span
          style={{
            flex: 1, minWidth: 0,
            fontSize: 'var(--evo-fs-caption)',
            fontWeight: 'var(--evo-list-header-fw)',
            letterSpacing: 'var(--evo-ls-caps)',
            textTransform: 'uppercase',
            color: 'var(--evo-fg-3)',
          }}
        >
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

// Fila de la bitácora de movimientos. No usa LogEntry: ese primitive es
// plegable y su `channel` es el canal de una conversación (email, Instagram,
// nota) — un movimiento de carta no es ninguno de esos y no tiene cuerpo que
// desplegar. Comparte la anatomía a través de los tokens --evo-log-*.
function EventRow({ event, stages, t }: {
  event: OpportunityEvent;
  stages: FunnelStage[];
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  const isSystem = event.actor !== 'user';
  const color = isSystem ? 'var(--evo-ch-system)' : 'var(--evo-ch-note)';
  const title = event.type === 'moved'
    ? t('opportunities.event.moved', { stage: stages.find(s => s.id === event.toStageId)?.name ?? '—' })
    : t(`opportunities.event.${event.type}`);

  return (
    <div
      style={{
        display: 'flex', alignItems: 'center',
        gap: 'var(--evo-log-icon-gap)',
        minHeight: 'var(--evo-log-row-min-h)',
        boxSizing: 'border-box',
        padding: 'var(--evo-log-row-py) var(--evo-log-row-px)',
        background: 'var(--evo-log-row-bg)',
        borderRadius: 'var(--evo-r-sm)',
      }}
    >
      <span
        style={{
          flexShrink: 0,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 'var(--evo-log-icon-sz)', height: 'var(--evo-log-icon-sz)',
          borderRadius: 'var(--evo-r-pill)',
          border: `1px solid ${color}`,
          color,
        }}
      >
        <Icon name={isSystem ? 'bolt' : 'pencil'} size={12} />
      </span>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <span style={{ fontSize: 'var(--evo-list-title-fs)', color: 'var(--evo-fg-2)' }}>{title}</span>
        <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)' }}>
          {t(`opportunities.actor.${event.actor}`)}
        </span>
      </div>
      <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
        {parseServerDate(event.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
    </div>
  );
}

// ── Panel ─────────────────────────────────────────────────────────────────────

export function OpportunityPanel({
  opportunityId, onClose, onLinkContact, onCreateEstimate, onCreateProject,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading } = useOpportunity(opportunityId);
  const move = useMoveOpportunity();
  const remove = useDeleteOpportunity();
  const actualizar = useUpdateOpportunity();

  const opportunity = data?.opportunity;
  const stages: FunnelStage[] = data?.stages ?? [];
  const stage = stages.find(s => s.id === opportunity?.stageId);

  function moveTo(stageId: number) {
    if (!opportunity) return;
    move.mutate({ id: opportunity.id, stageId, index: 0 }, {
      onError: () => toast.error(t('opportunities.moveFailed')),
    });
  }

  // Cerrar = mover a la primera etapa con ese resultado. El resultado vive en
  // la etapa, no en la carta (Board Pattern): sin una etapa marcada no hay a
  // dónde llevarla, y decirlo es mejor que inventar un estado sin columna.
  function closeWith(outcome: 'won' | 'lost') {
    const target = stages.find(s => s.outcome === outcome);
    if (!target) {
      toast.error(t('funnels.editor.deleteBlocked'));
      return;
    }
    moveTo(target.id);
  }

  return (
    <div
      style={{
        width: 'var(--evo-board-panel-w)',
        flexShrink: 0,
        borderLeft: '1px solid var(--evo-stroke)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
        background: 'var(--evo-bg-3)',
      }}
    >
      <div
        style={{
          flexShrink: 0,
          height: 'var(--evo-toolbar-height)',
          boxSizing: 'border-box',
          padding: '0 var(--evo-space-4) 0 var(--evo-space-5)',
          display: 'flex', alignItems: 'center', gap: 'var(--evo-space-3)',
          borderBottom: '1px solid var(--evo-stroke)',
          background: 'var(--evo-bg-2)',
        }}
      >
        <span
          style={{
            flex: 1, minWidth: 0,
            fontSize: 'var(--evo-fs-caption)',
            fontWeight: 'var(--evo-list-header-fw)',
            letterSpacing: 'var(--evo-ls-caps)',
            textTransform: 'uppercase',
            color: 'var(--evo-fg-3)',
          }}
        >
          {t('opportunities.label')}
        </span>
        {opportunity && (
          <Pill
            variant="outline"
            size="sm"
            tone={opportunity.status === 'won' ? 'success' : opportunity.status === 'lost' ? 'danger' : 'muted'}
          >
            {t(`opportunities.status.${opportunity.status}`)}
          </Pill>
        )}
        <IconButton
          icon={<Icon name="close" size={14} />}
          ariaLabel={t('common.close')}
          variant="ghost"
          size="sm"
          onClick={onClose}
        />
      </div>

      {isLoading || !opportunity ? (
        <LoadingState />
      ) : (
        <>
          <div
            style={{
              flex: 1, minHeight: 0,
              overflowY: 'auto',
              padding: 'var(--evo-pane-pad)',
              display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-5)',
            }}
          >
            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
              <h2 style={{ margin: 0, fontSize: 'var(--evo-fs-h3)', fontWeight: 400, color: 'var(--evo-fg-1)', lineHeight: 1.25 }}>
                {opportunity.title}
              </h2>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--evo-space-3)' }}>
                <ValorDeLaCarta
                  opportunityId={opportunity.id}
                  valueCents={opportunity.valueCents}
                  currency={opportunity.currency}
                  guardando={actualizar.isPending}
                  onGuardar={valueCents => actualizar.mutate({ id: opportunity.id, valueCents })}
                />
                {opportunity.formName != null && (
                  <span style={{ fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)' }}>
                    {opportunity.formName}
                  </span>
                )}
              </div>
              {/* Alternativa sin arrastre — táctil, teclado y lector de pantalla. */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)' }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <Dropdown<number>
                    value={opportunity.stageId}
                    options={stages.map(s => ({ value: s.id, label: s.name }))}
                    onChange={moveTo}
                    size="sm"
                    triggerStyle={{ width: '100%' }}
                  />
                </div>
                <span style={{ flexShrink: 0, fontSize: 'var(--evo-fs-caption)', color: 'var(--evo-fg-4)', whiteSpace: 'nowrap' }}>
                  {t('opportunities.stageSince', { age: relTime(opportunity.stageEnteredAt, t as (k: string, opts?: Record<string, unknown>) => string) })}
                </span>
              </div>
            </div>

            <PanelCard title={t('opportunities.links')}>
              <div style={{ padding: 'var(--evo-space-3) var(--evo-space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-2)' }}>
                {opportunity.submissionId != null && (
                  <LinkRow
                    icon="forms"
                    label={t('opportunities.link.form')}
                    value={opportunity.formName}
                    emptyLabel="—"
                    actionLabel={t('opportunities.link.formAction')}
                    onAction={() => navigate('/formularios')}
                  />
                )}
                <LinkRow
                  icon="contacts"
                  label={t('opportunities.link.contact')}
                  value={opportunity.contactName}
                  emptyLabel={t('opportunities.link.contactEmpty')}
                  actionLabel={t('opportunities.link.contactAction')}
                  onAction={() => {
                    if (opportunity.contactId != null) navigate(`/contacts/${opportunity.contactId}`);
                    else onLinkContact(opportunity.id);
                  }}
                />
                <LinkRow
                  icon="billing"
                  label={t('opportunities.link.estimate')}
                  value={opportunity.estimateNumber}
                  emptyLabel={t('opportunities.link.estimateEmpty')}
                  actionLabel={t('opportunities.link.create')}
                  onAction={() => {
                    if (opportunity.estimateId != null) navigate(`/facturacion/estimate/${opportunity.estimateId}`);
                    else onCreateEstimate(opportunity.id);
                  }}
                />
                <LinkRow
                  icon="projects"
                  label={t('opportunities.link.project')}
                  value={opportunity.projectName}
                  emptyLabel={t('opportunities.link.projectEmpty')}
                  actionLabel={t('opportunities.link.create')}
                  onAction={() => {
                    if (opportunity.projectId != null) navigate(`/proyectos/${opportunity.projectId}`);
                    else onCreateProject(opportunity.id);
                  }}
                />
              </div>
            </PanelCard>

            <PanelCard title={t('opportunities.data')}>
              <div style={{ padding: '0 var(--evo-space-4)' }}>
                <KVRow label={t('opportunities.field.stage')} value={stage?.name ?? '—'} />
                <KVRow
                  label={t('opportunities.field.source')}
                  value={opportunity.source === 'form'
                    ? t('opportunities.source.formAuto')
                    : t(`opportunities.source.${opportunity.source}`)}
                />
                <KVRow
                  label={t('opportunities.field.created')}
                  value={parseServerDate(opportunity.createdAt).toLocaleString()}
                />
                <KVRow label={t('opportunities.field.notes')} value={opportunity.notes ?? '—'} last />
              </div>
            </PanelCard>

            <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 'var(--evo-space-3)' }}>
              <span
                style={{
                  fontSize: 'var(--evo-fs-caption)',
                  fontWeight: 'var(--evo-list-header-fw)',
                  letterSpacing: 'var(--evo-ls-caps)',
                  textTransform: 'uppercase',
                  color: 'var(--evo-fg-3)',
                }}
              >
                {t('opportunities.events')}
              </span>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--evo-log-gap)' }}>
                {(data?.events ?? []).map(event => (
                  <EventRow key={event.id} event={event} stages={stages} t={t as (k: string, opts?: Record<string, unknown>) => string} />
                ))}
              </div>
            </div>
          </div>

          <div
            style={{
              flexShrink: 0,
              padding: 'var(--evo-space-3) var(--evo-space-5)',
              borderTop: '1px solid var(--evo-stroke)',
              display: 'flex', alignItems: 'center', gap: 'var(--evo-space-2)',
              background: 'var(--evo-bg-2)',
            }}
          >
            <div style={{ flex: 1 }}>
              <Button
                variant="outline"
                tone="cyan"
                fullWidth
                icon={<Icon name="check" size={12} />}
                onClick={() => closeWith('won')}
              >
                {t('opportunities.markWon')}
              </Button>
            </div>
            <div style={{ flex: 1 }}>
              <Button
                variant="outline"
                tone="danger"
                fullWidth
                icon={<Icon name="close" size={12} />}
                onClick={() => closeWith('lost')}
              >
                {t('opportunities.markLost')}
              </Button>
            </div>
            <IconButton
              icon={<Icon name="trash" size={14} />}
              ariaLabel={t('opportunities.delete')}
              variant="outline"
              tone="danger"
              loading={remove.isPending}
              onClick={() => { remove.mutate(opportunity.id, { onSuccess: onClose }); }}
            />
          </div>
        </>
      )}
    </div>
  );
}
